import { Column, ColumnContent, cn, Tabs, TabsContent } from "@qw/ui";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useBook } from "@/providers/use-book";
import { BookHeader } from "./book-header";
import { BookList } from "./book-list";
import { BookNavigationContext } from "./book-navigation";
import { BookResults } from "./book-results";
import { BookScopeRow } from "./book-scope-row";
import { SelectedInsuredRow } from "./selected-insured-row";

export const Book = ({ rootPath, withBorder = true }: { rootPath?: string; withBorder?: boolean }) => {
  const {
    bindRequested,
    bound,
    brokerReview,
    delivered,
    informationGathering,
    isBookOpen,
    isBookViewExpanded,
    lost,
    pending,
    quoted,
    quotedRenewals,
    upcomingRenewals,
    submitted,
    submittedRenewals,
    tab,
    unsubmitted,
    solicitedUnsubmittedRenewals,
    unsolicitedUnsubmittedRenewals,
    won,
    setTab,
  } = useBook();
  // The book answers to the committed `?term=` only; the search box holds its in-progress term
  // until the broker presses Enter.
  const { searchTerm } = useInboxQueryStates();

  if (!isBookOpen) {
    return null;
  }

  return (
    <BookNavigationContext.Provider value={rootPath}>
      <Tabs defaultValue={tab} onValueChange={setTab} value={searchTerm ? "results" : tab} asChild>
        <Column className={cn("@container bg-background gap-0", withBorder && "border-r")}>
          <BookHeader />
          {/* Above the sections and outside the scroll area, so the way back to the
              insured — and the reason the column is short under a scope chip — are
              the top lines of the Book and stay there. Hidden with the rest of the
              body when the book view is collapsed: `LeftSidebar` clips the panel to
              the header's own height, so there is nowhere to show them. Each renders
              on its own condition and only one can be true at a time — the pin needs
              a route insured, and a set scope is the absence of one. */}
          {isBookViewExpanded && (
            <>
              <SelectedInsuredRow />
              <BookScopeRow />
            </>
          )}
          <ColumnContent hidden={!isBookViewExpanded}>
            <TabsContent value="new">
              <BookList label="Marketing: Information Gathering" items={informationGathering} />
              <BookList label="Marketing: Broker Review" items={brokerReview} />
              <BookList label="Marketing: Unsubmitted" items={unsubmitted} />
              <BookList label="Marketing: Submitted" items={submitted} />
              <BookList label="Marketing: Quoted" items={quoted} />
            </TabsContent>
            <TabsContent value="renewals">
              <BookList label="Renewals: Upcoming" items={upcomingRenewals} variant="renewal" />
              <BookList label="Renewals: Unsolicited" items={unsolicitedUnsubmittedRenewals} variant="renewal" />
              <BookList label="Renewals: Solicited" items={solicitedUnsubmittedRenewals} variant="renewal" />
              <BookList label="Renewals: Submitted" items={submittedRenewals} variant="renewal" />
              <BookList label="Renewals: Quoted" items={quotedRenewals} variant="renewal" />
            </TabsContent>
            <TabsContent value="quotes">
              <BookList label="Quote: Pending" items={pending} />
              <BookList label="Quote: Delivered" items={delivered} />
              <BookList label="Quote: Bind Requested" items={bindRequested} />
              <BookList label="Quote: Bound" items={bound} />
            </TabsContent>
            <TabsContent value="won">
              {Object.keys(won).map((quarter) => (
                <BookList key={quarter} label={`Won ${quarter}`} items={won[quarter]} />
              ))}
            </TabsContent>
            <TabsContent value="lost">
              {Object.keys(lost).map((quarter) => (
                <BookList key={quarter} label={`Lost ${quarter}`} items={lost[quarter]} />
              ))}
            </TabsContent>
            <TabsContent value="results">
              <BookResults />
            </TabsContent>
          </ColumnContent>
        </Column>
      </Tabs>
    </BookNavigationContext.Provider>
  );
};
